import React, { useRef, useState, useCallback } from "react";

/**
 * Absolute-positioned overlay that lets a reviewer drag a rectangle to
 * annotate a specific region. Coordinates are normalized (0..1) against
 * the container box so they render consistently across zooms/rotations.
 *
 * Props:
 *  - annotations: [{ id, page, x, y, w, h, remark, author_role, author_name, status }]
 *  - page: current page (for PDFs) or null for images
 *  - canDraw: whether the current user can create annotations
 *  - onCreate({ x, y, w, h }): called after user confirms remark
 *  - onResolve(id): mark annotation resolved
 */
export default function AnnotationOverlay({ annotations = [], page = null, canDraw = false, onCreate, onResolve }) {
  const wrapRef = useRef(null);
  const [drag, setDrag] = useState(null); // {sx, sy, ex, ey} in px relative to wrap
  const [pending, setPending] = useState(null); // {x,y,w,h,remark}
  const [hoverId, setHoverId] = useState(null);

  const getPos = useCallback((e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, w: rect.width, h: rect.height };
  }, []);

  const onDown = (e) => {
    if (!canDraw || pending) return;
    if (e.button !== 0) return;
    e.preventDefault();
    const p = getPos(e);
    setDrag({ sx: p.x, sy: p.y, ex: p.x, ey: p.y });
  };
  const onMove = (e) => {
    if (!drag) return;
    const p = getPos(e);
    setDrag({ ...drag, ex: p.x, ey: p.y });
  };
  const onUp = (e) => {
    if (!drag) return;
    const p = getPos(e);
    const rect = wrapRef.current.getBoundingClientRect();
    const x1 = Math.max(0, Math.min(drag.sx, p.x));
    const y1 = Math.max(0, Math.min(drag.sy, p.y));
    const x2 = Math.min(rect.width, Math.max(drag.sx, p.x));
    const y2 = Math.min(rect.height, Math.max(drag.sy, p.y));
    const w = x2 - x1;
    const h = y2 - y1;
    setDrag(null);
    if (w < 8 || h < 8) return;
    setPending({
      x: x1 / rect.width,
      y: y1 / rect.height,
      w: w / rect.width,
      h: h / rect.height,
      remark: "",
    });
  };

  const confirmPending = () => {
    if (!pending || !pending.remark.trim()) return;
    onCreate?.({ x: pending.x, y: pending.y, w: pending.w, h: pending.h, remark: pending.remark.trim() });
    setPending(null);
  };
  const cancelPending = () => setPending(null);

  const visibleAnn = annotations.filter((a) => (page == null ? true : a.page === page));

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0"
      style={{ cursor: canDraw ? "crosshair" : "default", userSelect: "none" }}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={() => drag && setDrag(null)}
      data-testid="annotation-overlay"
    >
      {/* Existing annotations */}
      {visibleAnn.map((a) => (
        <div
          key={a.id}
          onMouseEnter={() => setHoverId(a.id)}
          onMouseLeave={() => setHoverId(null)}
          className="absolute pointer-events-auto"
          style={{
            left: `${a.x * 100}%`,
            top: `${a.y * 100}%`,
            width: `${a.w * 100}%`,
            height: `${a.h * 100}%`,
            border: `1.5px solid ${a.status === "resolved" ? "#10b981" : "#d4af37"}`,
            background: a.status === "resolved" ? "rgba(16,185,129,0.10)" : "rgba(212,175,55,0.14)",
            borderRadius: 3,
          }}
          data-testid={`annotation-${a.id}`}
        >
          {hoverId === a.id && (
            <div
              className="absolute z-20 min-w-[220px] max-w-[320px] bg-[#0a0a0a] border border-[#d4af37]/40 rounded-md p-3 text-[11px] text-gray-200 shadow-xl"
              style={{ top: "100%", left: 0, marginTop: 4 }}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-[9px] font-mono uppercase tracking-widest text-[#d4af37]">
                  {a.author_role} · {a.status}
                </p>
                {a.status !== "resolved" && onResolve && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onResolve(a.id); }}
                    className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 hover:text-emerald-300"
                    data-testid={`resolve-${a.id}`}
                  >
                    Resolve
                  </button>
                )}
              </div>
              <p className="text-[11px] text-white leading-relaxed">{a.remark}</p>
              <p className="text-[9px] text-gray-500 mt-1 font-mono">{a.author_name}</p>
            </div>
          )}
        </div>
      ))}

      {/* Live drag rectangle */}
      {drag && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: Math.min(drag.sx, drag.ex),
            top: Math.min(drag.sy, drag.ey),
            width: Math.abs(drag.ex - drag.sx),
            height: Math.abs(drag.ey - drag.sy),
            border: "1.5px dashed #d4af37",
            background: "rgba(212,175,55,0.15)",
          }}
        />
      )}

      {/* Pending annotation remark modal */}
      {pending && (
        <div
          className="absolute pointer-events-auto z-30"
          style={{
            left: `${pending.x * 100}%`,
            top: `${(pending.y + pending.h) * 100}%`,
            transform: "translateY(6px)",
            minWidth: 260,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <div className="bg-[#0a0a0a] border border-[#d4af37]/50 rounded-md p-3 shadow-2xl">
            <p className="text-[9px] font-mono uppercase tracking-widest text-[#d4af37] mb-2">Add remark</p>
            <textarea
              autoFocus
              rows={3}
              value={pending.remark}
              onChange={(e) => setPending({ ...pending, remark: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) confirmPending();
                if (e.key === "Escape") cancelPending();
              }}
              placeholder="Describe the issue with this region…"
              className="w-full bg-black border border-[#262626] rounded-md px-2 py-1.5 text-xs text-white placeholder-gray-600 focus:border-[#d4af37] outline-none resize-none"
              data-testid="annotation-remark-input"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={cancelPending} className="text-[10px] font-mono uppercase text-gray-500 hover:text-gray-300 px-2 py-1">
                Cancel
              </button>
              <button
                onClick={confirmPending}
                disabled={!pending.remark.trim()}
                className="text-[10px] font-mono uppercase tracking-widest bg-[#d4af37] text-black px-2.5 py-1 rounded disabled:opacity-40"
                data-testid="annotation-remark-save"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
